/**
 * DecisionBar — Accept / Change / Escalate for one clause's review
 * (§13.7's third Verify assertion, session 4 FACTS 7). Each button is its own
 * `PUT /clauses/:clauseReviewId/decision` — hand-rolled (not the shared
 * `SegmentedControl`) so each option keeps its own stable `data-testid`
 * (`ctr-decide-<decision>-<reviewId>`), styled with the same `segBtnCls`
 * track/active recipe `SegmentedControl` itself uses (design skill).
 *
 * A clause with `review: null` never reaches this component — `ClauseRow.tsx`
 * shows "Not reviewed yet" instead; the version-level "Run review" action
 * covers it.
 */
import { segBtnCls } from '../../../ui/styles';
import type { ClauseDecision, ClauseReview } from '../../api/clauses';

interface DecisionBarProps {
  review: ClauseReview;
  deciding: boolean;
  onDecide: (reviewId: string, decision: ClauseDecision) => void;
}

const OPTIONS: Array<{ value: ClauseDecision; label: string }> = [
  { value: 'accept', label: 'Accept' },
  { value: 'change', label: 'Change' },
  { value: 'escalate', label: 'Escalate' },
];

export function DecisionBar({ review, deciding, onDecide }: DecisionBarProps) {
  return (
    <div
      role="group"
      aria-label="Decision"
      title={review.decided_at ? `decided ${review.decided_at}` : 'not yet decided'}
      className="inline-flex items-center gap-0.5 rounded-lg border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900/40"
    >
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`ctr-decide-${o.value}-${review.id}`}
          disabled={deciding}
          onClick={() => onDecide(review.id, o.value)}
          aria-pressed={review.decision === o.value}
          className={`${segBtnCls(review.decision === o.value)} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default DecisionBar;
